import type { ManagerTenantSummary, SubscriptionState } from '@odonix/contracts';
import { money } from '@odonix/core';

/**
 * Слова кабинета менеджера. Менеджер говорит с клиникой по телефону, и строка
 * срока в списке — это первая фраза разговора: «пробный у вас до пятнадцатого,
 * осталось три дня».
 */

export const STATE_LABEL: Record<SubscriptionState, string> = {
  trial: 'Пробный период',
  active: 'Активна',
  grace: 'Ожидает оплаты',
  restricted: 'Запись закрыта',
  suspended: 'Приостановлена',
};

export const STATE_TONE: Record<SubscriptionState, 'info' | 'success' | 'warning' | 'danger'> = {
  trial: 'info',
  active: 'success',
  grace: 'warning',
  restricted: 'warning',
  suspended: 'danger',
};

export const KIND_LABEL: Record<'clinic' | 'lab', string> = {
  clinic: 'Клиника',
  lab: 'Лаборатория',
};

export function plural(n: number, forms: readonly [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

const DAYS = ['день', 'дня', 'дней'] as const;

export function shortDate(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

export function longDate(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function amount(minor: number): string {
  return money.format(money.kopecks(minor));
}

export interface Deadline {
  text: string;
  /** `danger` — три дня и меньше или уже прошло; `warning` — неделя. */
  tone: 'danger' | 'warning' | 'muted';
}

export function deadline(summary: Pick<ManagerTenantSummary, 'subscriptionState' | 'endsOn' | 'daysLeft'>): Deadline | null {
  const { subscriptionState: state, endsOn, daysLeft } = summary;
  if (state === 'suspended') return { text: 'Доступ закрыт', tone: 'danger' };
  if (endsOn === null || daysLeft === null) return null;

  const trial = state === 'trial';
  const date = shortDate(endsOn);

  if (daysLeft > 0) {
    return {
      text: `${trial ? 'Пробный до' : 'Оплачено до'} ${date} · осталось ${String(daysLeft)} ${plural(daysLeft, DAYS)}`,
      tone: daysLeft <= 3 ? 'danger' : daysLeft <= 7 ? 'warning' : 'muted',
    };
  }
  if (daysLeft === 0) return { text: `${trial ? 'Пробный' : 'Оплаченный период'} кончается сегодня`, tone: 'danger' };

  const ago = -daysLeft;
  return {
    text: `${trial ? 'Пробный кончился' : 'Оплата кончилась'} ${date} · ${String(ago)} ${plural(ago, DAYS)} назад`,
    tone: 'danger',
  };
}

/** Номер для `tel:` — цифры и плюс: звонилка не понимает скобок и пробелов. */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

export const PAYMENT_STATUS_LABEL: Record<'pending' | 'succeeded' | 'canceled' | 'failed', string> = {
  pending: 'в обработке',
  succeeded: 'оплачено',
  canceled: 'отменено',
  failed: 'не прошла',
};
